// CustomDomainTab.tsx
import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAction, useSubscriptionActions } from '../../hooks/useActions';
import { useAddCustomHostname, useCustomHostnameDns, useRemoveCustomHostname } from '../../hooks/useCustomHostname';

interface CustomDomainTabProps {
  subscriptionId: string;
  hostname: string;
  customHostnames: string[];
  maxCustomHostnames: number;
  /** The controller's current inform override host (from metrics); null/undefined = none known. */
  informOverrideHost?: string | null;
  /** UOS service: check each domain's CNAME and flag one still pointing at the old cloudunifi.com name. */
  checkDns?: boolean;
  /**
   * A platform-specific line under the intro, with its guide link. Set for a node UISP
   * (UISP_CUSTOM_DOMAIN_NOTE): UISP puts its configured hostname into device connection
   * strings, so the customer must also set the new name inside UISP. Absent for UniFi.
   */
  platformNote?: { text: string; helpUrl: string };
}

export const HELP_CHANGE_CUSTOM_DOMAIN_URL = 'https://help.cloudunifi.com/Unifi/custom-domain/#changing-your-custom-domain';
export const HELP_UISP_CUSTOM_DOMAIN_URL = 'https://help.cloudunifi.com/uisp/custom-domain/';
// eslint-disable-next-line react-refresh/only-export-components -- object constant; the service page and its test import the UISP note from this module
export const UISP_CUSTOM_DOMAIN_NOTE = {
  text: 'Once the domain shows Active here, open UISP, go to Settings and set the same name as the UISP hostname, so your devices connect through it.',
  helpUrl: HELP_UISP_CUSTOM_DOMAIN_URL,
};

function sameHost(a: string | null | undefined, b: string): boolean {
  if (!a) return false;
  const norm = (h: string) => h.trim().replace(/\.+$/, '').toLowerCase();
  return norm(a) === norm(b);
}

/** Explains why a domain can't be removed yet and the safe order of operations. */
export function InformHostInUseNotice({ informHost }: { informHost: string }) {
  return (
    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" role="alert">
      <p className="text-sm font-medium text-amber-900">
        {informHost} is still in use by your UniFi devices
      </p>
      <p className="text-sm text-amber-800 mt-1">
        Your controller is currently telling every device to inform to this domain. Removing it now
        would disconnect any device that has not yet re-provisioned. Change it in this order:
      </p>
      <ol className="list-decimal list-inside mt-2 space-y-1 text-sm text-amber-800">
        <li>
          In UniFi Network go to <span className="font-medium">Settings → System → Advanced</span> and
          set <span className="font-medium">Inform Host</span> to the hostname you want to keep (add the
          new domain here first if you are switching domains).
        </li>
        <li>
          Wait until every site shows all devices connected. Devices pick up the new inform host on
          their next check-in, usually within a few minutes — offline devices only when they come back.
        </li>
        <li>Come back here and remove this domain.</li>
      </ol>
      <a
        href={HELP_CHANGE_CUSTOM_DOMAIN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-2 text-sm text-amber-900 underline"
      >
        Read the step-by-step guide
      </a>
    </div>
  );
}

const DOMAIN_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

export default function CustomDomainTab({
  subscriptionId,
  hostname,
  customHostnames,
  maxCustomHostnames,
  informOverrideHost = null,
  checkDns = false,
  platformNote,
}: CustomDomainTabProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set when the API refused a removal because the domain is the controller's inform host.
  const [informHostBlock, setInformHostBlock] = useState<string | null>(null);
  const [localActionId, setLocalActionId] = useState<string | null>(null);
  const [activeWarning, setActiveWarning] = useState<{
    type: 'ip_detected' | 'wildcard_conflict';
    ip: string;
    message: string;
  } | null>(null);
  const [localPendingHostname, setLocalPendingHostname] = useState<string | null>(null);
  const [localIsRemoving, setLocalIsRemoving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const queryClient = useQueryClient();
  const { data: subscriptionActions } = useSubscriptionActions(subscriptionId);
  const addCustomHostname = useAddCustomHostname();
  const removeCustomHostname = useRemoveCustomHostname();
  const dns = useCustomHostnameDns(subscriptionId, customHostnames, checkDns && customHostnames.length > 0);
  const dnsTarget = dns.data?.target ?? null;
  // Domains that work but still reach the console through the old <slug>.cloudunifi.com name
  const legacyHop = (ch: string) =>
    dns.data?.hostnames.find((d) => d.status === 'legacy' && sameHost(d.hostname, ch))?.found ?? null;

  // Find pending custom_tls action from subscription actions
  const existingPendingAction = useMemo(() => {
    if (!subscriptionActions) return null;
    return subscriptionActions.find(
      (a) =>
        a.action_type === 'custom_tls' &&
        (a.status === 'pending' || a.status === 'claimed' || a.status === 'running')
    );
  }, [subscriptionActions]);

  // Derive the active action ID - prefer local state, fall back to existing pending action
  const activeActionId = localActionId || (dismissed ? null : existingPendingAction?.id) || null;

  // Derive operation info from existing pending action
  const existingActionInfo = useMemo(() => {
    if (!existingPendingAction) return null;
    try {
      const payload = JSON.parse(existingPendingAction.action_payload || '{}');
      return {
        isRemoving: payload.operation === 'remove',
        pendingHostname: payload.custom_hostname || null,
      };
    } catch {
      return null;
    }
  }, [existingPendingAction]);

  // Final derived values - use local state if set, otherwise existing action info
  const isRemoving = localActionId ? localIsRemoving : (existingActionInfo?.isRemoving ?? false);
  const pendingHostname = localActionId ? localPendingHostname : existingActionInfo?.pendingHostname;

  const { data: activeAction } = useAction(activeActionId);

  // Clear action tracking and refresh data when action completes
  useEffect(() => {
    if (activeAction?.status === 'completed' || activeAction?.status === 'failed') {
      // Refresh subscription data to get updated custom_hostnames
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });

      // Keep showing status for a moment then clear local state
      const timer = setTimeout(() => {
        if (activeAction?.status === 'completed') {
          setLocalActionId(null);
          setLocalPendingHostname(null);
          setLocalIsRemoving(false);
          setDismissed(false);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [activeAction?.status, queryClient]);

  const isProcessing = activeAction?.status === 'pending' ||
    activeAction?.status === 'claimed' ||
    activeAction?.status === 'running';

  // Block all add/remove while any custom_tls action is in-flight
  const hasInflightAction = isProcessing || !!pendingHostname;

  const validateInput = (value: string): string | null => {
    if (!value.trim()) {
      return 'Please enter a domain name';
    }
    if (!DOMAIN_REGEX.test(value)) {
      return 'Please enter a valid domain name (e.g., unifi.example.com)';
    }
    return null;
  };

  const handleSubmit = async (confirmations?: { ip_warning?: boolean; wildcard_warning?: boolean }) => {
    setError(null);
    setActiveWarning(null);

    const validationError = validateInput(inputValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    const result = await addCustomHostname.mutateAsync({
      subscriptionId,
      hostname: inputValue,
      confirmations,
    });

    if (result.success) {
      setLocalActionId(result.data.action_id);
      setLocalPendingHostname(inputValue);
      setLocalIsRemoving(false);
      setDismissed(false);
      setInputValue('');
    } else if (result.warning) {
      setActiveWarning({ type: result.warning, ip: result.ip!, message: result.message });
    } else {
      setError(result.message);
    }
  };

  const handleRemove = async (hostnameToRemove: string) => {
    setError(null);
    setInformHostBlock(null);
    const result = await removeCustomHostname.mutateAsync({
      subscriptionId,
      hostname: hostnameToRemove,
    });

    if (result.success) {
      setLocalActionId(result.data!.action_id);
      setLocalPendingHostname(hostnameToRemove);
      setLocalIsRemoving(true);
      setDismissed(false);
    } else if (result.error === 'inform_host_in_use') {
      setInformHostBlock(result.details?.inform_host || hostnameToRemove);
    } else {
      setError(result.message || 'Failed to remove custom hostname');
    }
  };

  const handleDismiss = () => {
    setLocalActionId(null);
    setLocalPendingHostname(null);
    setLocalIsRemoving(false);
    setDismissed(true);
    setError(null);
    setInformHostBlock(null);
  };

  const canAddMore = customHostnames.length < maxCustomHostnames && !hasInflightAction;

  const sectionTitle = maxCustomHostnames > 1 ? 'Custom Domains' : 'Custom Domain';

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{sectionTitle}</h2>
      <p className={`text-sm text-gray-500 ${platformNote ? 'mb-3' : 'mb-6'}`}>
        Access your controller using your own domain name
      </p>
      {platformNote && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-900">{platformNote.text}</p>
          <a
            href={platformNote.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-sm text-blue-700 underline"
          >
            Custom domain guide
          </a>
        </div>
      )}

      {/* Default hostname */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <div>
            <p className="font-medium text-gray-900">{hostname}</p>
            <p className="text-sm text-gray-500">Default - Cannot be removed</p>
          </div>
        </div>
      </div>

      {/* Existing custom hostnames */}
      {customHostnames.map((ch) => {
        const isPendingAction = pendingHostname === ch;
        const isInformHost = sameHost(informOverrideHost, ch);
        return (
          <div key={ch} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <div>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    {ch}
                    {isInformHost && (
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                        title="Your controller tells UniFi devices to inform to this domain"
                      >
                        Inform host
                      </span>
                    )}
                  </p>
                  {isPendingAction && isProcessing ? (
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                      {isRemoving ? 'Removing TLS certificate...' : 'Setting up TLS certificate...'}
                    </div>
                  ) : isPendingAction && activeAction?.status === 'failed' ? (
                    <p className="text-sm text-red-600">{activeAction.result || 'Operation failed'}</p>
                  ) : (
                    <>
                      <p className="text-sm text-green-600">Active</p>
                      {dnsTarget && legacyHop(ch) && (
                        <p className="text-sm text-amber-700 mt-0.5">
                          DNS update needed: this domain still points to{' '}
                          <span className="font-mono">{legacyHop(ch)}</span>. Change its CNAME record to{' '}
                          <span className="font-mono">{dnsTarget}</span>.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              {!hasInflightAction && !isInformHost && (
                <button
                  onClick={() => handleRemove(ch)}
                  disabled={removeCustomHostname.isPending}
                  className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
              {!hasInflightAction && isInformHost && (
                <button
                  type="button"
                  onClick={() => setInformHostBlock(ch)}
                  className="text-sm text-gray-400 cursor-not-allowed"
                  aria-disabled="true"
                  title="This is your controller's inform host — see why it can't be removed yet"
                >
                  In use
                </button>
              )}
              {isPendingAction && (activeAction?.status === 'completed' || activeAction?.status === 'failed') && (
                <button
                  onClick={handleDismiss}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Pending add hostname (not yet in customHostnames list) */}
      {pendingHostname && !isRemoving && !customHostnames.includes(pendingHostname) && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <div>
                <p className="font-medium text-gray-900">{pendingHostname}</p>
                {isProcessing ? (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                    Setting up TLS certificate...
                  </div>
                ) : activeAction?.status === 'completed' ? (
                  <p className="text-sm text-green-600">Active</p>
                ) : activeAction?.status === 'failed' ? (
                  <p className="text-sm text-red-600">{activeAction.result || 'Setup failed'}</p>
                ) : null}
              </div>
            </div>
            {(activeAction?.status === 'completed' || activeAction?.status === 'failed') && (
              <button
                onClick={handleDismiss}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* Limit indicator — only show when usage is notable */}
      {maxCustomHostnames > 1 && customHostnames.length >= 10 && (
        <p className="text-sm text-gray-500 mb-4">
          {customHostnames.length} of {maxCustomHostnames} custom domains used
        </p>
      )}

      {/* Inform-host block — explains the safe order instead of a bare error */}
      {informHostBlock && <InformHostInUseNotice informHost={informHostBlock} />}

      {/* Error display */}
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {/* Add domain form - only show when under the limit and no in-flight action */}
      {canAddMore && (
        <>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add your own domain
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError(null);
                }}
                placeholder="unifi.example.com"
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <button
                onClick={() => handleSubmit()}
                disabled={addCustomHostname.isPending || !inputValue.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addCustomHostname.isPending ? 'Adding...' : 'Add Domain'}
              </button>
            </div>
          </div>

          {/* DNS Warning Dialog */}
          {activeWarning && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 mb-3">{activeWarning.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleSubmit(
                    activeWarning.type === 'ip_detected'
                      ? { ip_warning: true }
                      : { wildcard_warning: true }
                  )}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                >
                  Proceed Anyway
                </button>
                <button
                  onClick={() => setActiveWarning(null)}
                  className="px-3 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              To add a custom domain, follow these steps:
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
              <li>Log in to your domain registrar's DNS management panel</li>
              <li>
                Add a CNAME record pointing to: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-gray-900">{hostname}</code>
              </li>
              <li>Wait for DNS propagation (usually a few minutes, up to 48 hours)</li>
              <li>Enter your domain above and click "Add Domain"</li>
            </ol>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-1">Why use a CNAME record?</h4>
              <p className="text-sm text-blue-700">
                A CNAME record points to your server's hostname rather than its IP address.
                If we ever need to move your server to a different IP, your custom domain will
                continue working automatically.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
